import { Box, Typography, Paper, Alert } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import { Button } from 'framework/ui/mui-builder';
import { DocumentPreviewRenderer, useDocumentBlob } from 'domain/shared/documentStorage';
import { useShowContext } from 'framework/show';

const DocumentPreviewContent = () => {
  const { data } = useShowContext();
  const documentPath = data?.documentPath;

  const { blobUrl, loading, hasError } = useDocumentBlob(documentPath);

  const handlePrint = () => {
    if (!blobUrl) return;
    const printWindow = window.open(blobUrl);
    if (!printWindow) return;
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
    };
  };

  const handleSave = () => {
    if (!blobUrl || !data?.fileName) return;

    const fileNameWithoutExtension = data.fileName.replace(/\.[^/.]+$/, '');
    const pdfFileName = `${fileNameWithoutExtension}.pdf`;

    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = pdfFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (data?.hasError) {
    return (
      <Box display="flex" flexDirection="column" height="100%" p={2}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load document. Please try again later.
        </Alert>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" height="100%" p={2}>
      <Paper elevation={0} sx={{ p: 2, mb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="h4" color="primary" mb={2}>
              {data.fileName}
            </Typography>
            {data?.office && (
              <Typography variant="body1" color="textSecondary">
                Office: {data.office}
              </Typography>
            )}
            {data?.section && (
              <Typography variant="body1" color="textSecondary">
                Section: {data.section}
              </Typography>
            )}
            {data?.fileType && (
              <Typography variant="body1" color="textSecondary">
                File Type: {data.fileType}
              </Typography>
            )}
            {data?.internalId && (
              <Typography variant="body1" color="textSecondary">
                Internal ID: {data.internalId}
              </Typography>
            )}
            {data?.templateId && (
              <Typography variant="body1" color="textSecondary">
                Template ID: {data.templateId}
              </Typography>
            )}
          </Box>

          <Box display="flex" gap={1}>
            <Button color="primary" onClick={handlePrint} disabled={!blobUrl} sx={{ alignSelf: 'flex-start' }}>
              <Typography mr="4px">Print</Typography>
              <PrintIcon sx={{ fontSize: 16 }} />
            </Button>
            <Button
              color="secondary"
              onClick={handleSave}
              disabled={!blobUrl || !data?.fileName}
              sx={{ alignSelf: 'flex-start' }}
            >
              <Typography mr="4px">Save PDF</Typography>
            </Button>
          </Box>
        </Box>
      </Paper>

      <Box flex={1}>
        <Box
          height="540px"
          width="100%"
          sx={{
            transition: 'all 0.3s ease',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <DocumentPreviewRenderer
            documentPath={documentPath!}
            blobUrl={blobUrl!}
            loading={loading}
            hasError={hasError}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default DocumentPreviewContent;
